import { cn } from '@/lib/utils';
import { Store, Clock, ShoppingBag } from 'lucide-react';
import { sanitizeRichTextHtml } from '@/lib/richtext/sanitizeRichTextHtml';

/**
 * FarmShopPagePreview — page-like, non-interactive preview that mirrors
 * the public farm shop page. Used in the editor's main (left) column.
 *
 * Follows the preview-first editorial standard:
 *   Hero → Title → Intro → Status/hours → Products → CTA (disabled)
 *
 * Design principle: "Editorial first, commerce second"
 * The shop must feel like a place you want to visit, not a product catalog.
 *
 * Driven entirely by local form state — no API calls.
 */

interface PreviewProduct {
  id: string;
  title: string;
  price_ore: number;
  currency: string;
  status: string;
  imageUrl?: string | null;
}

interface FarmShopPagePreviewProps {
  producerName: string;
  coverImageUrl?: string | null;
  shopIntro?: string | null;
  shopStatus?: string;
  openingHours?: string | null;
  products: PreviewProduct[];
}

function formatPreviewPrice(ore: number, currency: string = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(ore / 100);
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Öppet just nu',
  closed: 'Stängt just nu',
  seasonal: 'Säsongsöppet',
};

export function FarmShopPagePreview({
  producerName,
  coverImageUrl,
  shopIntro,
  shopStatus = 'closed',
  openingHours,
  products,
}: FarmShopPagePreviewProps) {
  const hasHero = !!coverImageUrl;
  const cleanIntro = shopIntro ? sanitizeRichTextHtml(shopIntro) : null;
  const statusLabel = STATUS_LABELS[shopStatus] || STATUS_LABELS.closed;
  const activeProducts = products.filter(p => p.status === 'public');
  const hasProducts = activeProducts.length > 0;

  return (
    <div className="rounded-2xl border border-border/30 bg-card shadow-sm overflow-hidden">
      {/* Hero — real image or collapse entirely */}
      {hasHero && (
        <div className="relative aspect-[3/2] sm:aspect-[2.2/1] overflow-hidden">
          <img
            src={coverImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10 pointer-events-none" />

          {/* Identity overlay */}
          <div className="absolute bottom-0 left-0 right-0 pb-6 px-8">
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="h-8 w-8 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Store className="h-4 w-4 text-white" />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-medium text-white leading-tight">
                Gårdsbutiken
              </h1>
            </div>
            <p className="text-white/70 text-sm">{producerName}</p>
          </div>
        </div>
      )}

      {/* Content area */}
      <div className={cn('px-8 pb-8 max-w-2xl', hasHero ? 'pt-5' : 'pt-8')}>
        {/* Title when no hero */}
        {!hasHero && (
          <>
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Store className="h-4 w-4 text-primary" />
              </div>
              <h1 className="font-display text-2xl font-medium text-foreground leading-tight">
                Gårdsbutiken
              </h1>
            </div>
            <p className="text-muted-foreground text-sm mb-4">{producerName}</p>
          </>
        )}

        {/* Status & opening hours */}
        {(shopStatus || openingHours) && (
          <div className="rounded-xl bg-muted/40 border border-border/30 px-4 py-3 mb-5">
            <div className="flex items-center gap-2 text-sm">
              <div
                className={cn(
                  'h-2 w-2 rounded-full',
                  shopStatus === 'open' ? 'bg-green-500' : 'bg-amber-500',
                )}
              />
              <span className="font-medium text-foreground">{statusLabel}</span>
            </div>
            {openingHours && (
              <div className="flex items-start gap-1.5 mt-1.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3 mt-0.5 shrink-0" />
                <span className="whitespace-pre-line">{openingHours}</span>
              </div>
            )}
          </div>
        )}

        {/* Intro — editorial storytelling */}
        {cleanIntro ? (
          <div
            className="prose prose-sm prose-muted max-w-none text-foreground/80 leading-relaxed mb-6"
            dangerouslySetInnerHTML={{ __html: cleanIntro }}
          />
        ) : (
          <p className="text-sm text-muted-foreground/40 italic leading-relaxed mb-6">
            Berätta vad besökare hittar här och hur det känns i gårdsbutiken…
          </p>
        )}

        {/* Products section heading */}
        {hasProducts && (
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Finns att ta med hem
          </h2>
        )}

        {/* Product grid — editorial, calm */}
        {hasProducts ? (
          <div className="grid grid-cols-2 gap-3">
            {activeProducts.slice(0, 6).map((p) => (
              <div
                key={p.id}
                className="rounded-xl border border-border/30 overflow-hidden bg-background"
              >
                {/* Product image */}
                <div className="aspect-square bg-muted overflow-hidden">
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-lg font-medium text-muted-foreground/40">
                        {(p.title?.[0] ?? '?').toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground line-clamp-1">{p.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {formatPreviewPrice(p.price_ore, p.currency)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-border/30 bg-muted/20 p-6 text-center">
            <ShoppingBag className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground/50">
              Inga produkter publicerade ännu
            </p>
          </div>
        )}

        {/* CTA button (disabled, non-interactive) */}
        <div className="mt-8">
          <button
            disabled
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary/80 text-primary-foreground px-6 py-2.5 text-sm font-medium opacity-60 cursor-default"
          >
            <ShoppingBag className="h-4 w-4" />
            Beställ från gården
          </button>
          <p className="text-[10px] text-muted-foreground/40 mt-1.5">
            Förhandsvisning — knappen är inte aktiv
          </p>
        </div>
      </div>
    </div>
  );
}
